import type { AppThunkAction, AppThunkDispatch } from "../app.types"
import type {
  FilterGroup,
  SearchActions,
  SearchFilterAction,
  SearchFilterState,
  SearchMapAction,
  SearchResultState,
  SearchSortAction,
  SearchSortValue,
  SearchSuggestionAction,
  SearchSuggestions,
  SearchValueAction
} from "./search.types"

import { AnalyticsSearchModule } from "../../models"
import { clearSearchCache, getSuggestions, searchMap } from "../../services/SearchService"
import type { AppState } from "../app.state"

export const SEARCH_CHANGE_VALUE: string = "SEARCH_CHANGE_VALUE"
export const SEARCH_MAP_START: string = "SEARCH_MAP_START"
export const SEARCH_MAP_LOAD: string = "SEARCH_MAP_LOAD"
export const SEARCH_CHANGE_FILTER: string = "SEARCH_CHANGE_FILTER"
export const SEARCH_CHANGE_SORT: string = "SEARCH_CHANGE_SORT"
export const SEARCH_UPDATE_SUGGESTIONS: string = "SEARCH_UPDATE_SUGGESTIONS"
export const SEARCH_LOAD_SUGGESTIONS: string = "SEARCH_LOAD_SUGGESTIONS"
export const SEARCH_CHANGE_PAGE: string = "SEARCH_CHANGE_PAGE"
export const SEARCH_CLEAR: string = "SEARCH_CLEAR"
export const SEARCH_CLEAR_FILTERS: string = "SEARCH_CLEAR_FILTERS"
export const SEARCH_SET_PRESERVE_STATE: string = "SEARCH_SET_PRESERVE_STATE"

export const SEARCH_CLEAR_CACHE: string = "SEARCH_CLEAR_CACHE"

export function changeSearchValue(searchValue: string): SearchValueAction {
  return { type: SEARCH_CHANGE_VALUE, payload: searchValue }
}

export function searchSetPreserveState(): AppThunkAction<void, SearchActions> {
  return (dispatch: AppThunkDispatch<SearchActions>) => {
    dispatch({ type: SEARCH_SET_PRESERVE_STATE, payload: true })
  }
}

export function performMapSearch(
  searchValue: string,
  searchModule: AnalyticsSearchModule,
  moduleId?: string
): AppThunkAction<void, SearchMapAction> {
  return async (dispatch: AppThunkDispatch<SearchMapAction>, getState: () => AppState) => {
    dispatch({ type: SEARCH_MAP_START })

    const state: AppState = getState()
    const page: number = state.search.searchResults.page
    const pageSize: number = state.search.searchResults.pageSize
    const filterState: SearchFilterState = state.search.filters
    const sortOptions: SearchSortValue = state.search.sortOrder

    const results: SearchResultState | undefined = await searchMap(
      searchValue,
      page,
      pageSize,
      filterState,
      sortOptions
    )

    if (results) {
      sendSearchTrackingData(searchValue, results, sortOptions, searchModule, filterState, moduleId)
      dispatch(loadMapSearchResults(results))
    }
  }
}

export function loadMapSearchResults(results: SearchResultState): SearchMapAction {
  return { type: SEARCH_MAP_LOAD, payload: results }
}

export function searchClear(): AppThunkAction<void, SearchActions> {
  return (dispatch: AppThunkDispatch<SearchActions>, getState: () => AppState) => {
    const preserve: boolean = getState().search.preserve
    preserve ? dispatch({ type: SEARCH_SET_PRESERVE_STATE, payload: false }) : dispatch({ type: SEARCH_CLEAR })
  }
}

export function searchClearFilters(): AppThunkAction<void, SearchFilterAction> {
  return (dispatch: AppThunkDispatch<SearchActions>, getState: () => AppState) => {
    const searchValue: string = getState().search.searchValue
    dispatch({ type: SEARCH_CLEAR_FILTERS })
    dispatch(performMapSearch(searchValue, AnalyticsSearchModule.Main))
  }
}

export function searchChangeFilter(
  filterGroup: FilterGroup,
  filters: string[]
): AppThunkAction<void, SearchFilterAction> {
  return (dispatch: AppThunkDispatch<SearchActions>, getState: () => AppState) => {
    const state: AppState = getState()
    if (filterGroup.name === "Type") {
      // biome-ignore lint/style/noParameterAssign: TODO in a later PR
      filters = setTypeFilters(state, filters)
    }
    const currentFilters: string[] = state.search.filters[filterGroup.name]
    dispatch({
      type: SEARCH_CHANGE_FILTER,
      payload: { filterGroup, filters }
    })
    const searchValue: string = state.search.searchValue

    const moduleInfo = getFilterModuleInfo(currentFilters, filterGroup, filters)

    dispatch(performMapSearch(searchValue, moduleInfo.module, moduleInfo.newFilter))
  }
}

export function searchChangeSortOrder(sortValue: SearchSortValue): AppThunkAction<void, SearchSortAction> {
  return (dispatch: AppThunkDispatch<SearchActions>, getState: () => AppState) => {
    dispatch({ type: SEARCH_CHANGE_SORT, payload: sortValue })
    const state: AppState = getState()
    const searchValue: string = state.search.searchValue

    dispatch(performMapSearch(searchValue, AnalyticsSearchModule.Main))
  }
}

export function updateSuggestions(searchValue: string): AppThunkAction<void, SearchSuggestionAction> {
  return async (dispatch: AppThunkDispatch<SearchSuggestionAction>) => {
    dispatch({ type: SEARCH_UPDATE_SUGGESTIONS })
    const suggestions: SearchSuggestions = await getSuggestions(searchValue)
    dispatch(loadSuggestions(suggestions))
  }
}

export function deleteSearchCache(): AppThunkAction<void, SearchActions> {
  return async (dispatch: AppThunkDispatch<SearchActions>) => {
    dispatch({ type: SEARCH_CLEAR_CACHE })
    await clearSearchCache()
  }
}

export function loadSuggestions(suggestions: SearchSuggestions): SearchSuggestionAction {
  return { type: SEARCH_LOAD_SUGGESTIONS, payload: suggestions }
}

export function searchChangePage(page: number): AppThunkAction<void, SearchActions> {
  return (dispatch: AppThunkDispatch<SearchActions>, getState: () => AppState) => {
    dispatch({ type: SEARCH_CHANGE_PAGE, payload: page })
    const state: AppState = getState()
    const searchValue: string = state.search.searchValue

    dispatch(performMapSearch(searchValue, AnalyticsSearchModule.Main))
  }
}

function setTypeFilters(state: AppState, filters: string[]): string[] {
  if (!state.search.filters || !state.search.filters.Type) return filters
  if (
    filters.findIndex((x) => x === "Papier en digitaal") !== -1 &&
    state.search.filters.Type.findIndex((x) => x === "Papier en digitaal") === -1
  ) {
    return ["Papier en digitaal"]
  }
  if (
    filters.findIndex((x) => x === "Alleen digitaal") !== -1 &&
    state.search.filters.Type.findIndex((x) => x === "Alleen digitaal") === -1
  ) {
    return ["Alleen digitaal"]
  }
  return filters
}

function sendSearchTrackingData(
  searchValue: string,
  results: SearchResultState,
  sortOptions: SearchSortValue,
  searchModule: AnalyticsSearchModule,
  filterState: SearchFilterState,
  moduleItemId?: string
): void {
  const sortDirection: "asc" | "desc" =
    sortOptions === "mapNumber" ? "asc" : sortOptions === "ascending" ? "asc" : "desc"

  window.tracker?.itemListFiltered({
    module: searchModule,
    itemId: moduleItemId,
    searchTerm: searchValue.toLowerCase(),
    pageSize: results.pageSize,
    pageNumber: results.page,
    type: "map",
    count: results.count,
    order: {
      direction: sortDirection,
      field: sortOptions === "mapNumber" ? "MapNumber" : "Title"
    },
    facets: {
      ...filterState
    }
  })
}

function getFilterModuleInfo(
  currentFilters: string[],
  filterGroup: FilterGroup,
  filters: string[]
): { module: AnalyticsSearchModule; newFilter?: string } {
  const addedFilters: string[] = filters.filter((x) => !currentFilters?.includes(x))

  return {
    module: getSearchModule(filterGroup.name),
    newFilter: addedFilters[0] ? addedFilters[0].toLowerCase() : undefined
  }
}

function getSearchModule(filterGroupName: string): AnalyticsSearchModule {
  switch (filterGroupName) {
    case "Regions":
      return AnalyticsSearchModule.RegionFilterGroup
    case "Themes":
      return AnalyticsSearchModule.ThemeFilterGroup
    case "Type":
      return AnalyticsSearchModule.TypeFilterGroup
    case "Categories":
      return AnalyticsSearchModule.CategoryFilterGroup
    default:
      throw new Error("module not supported")
  }
}
